import React, { useState, useEffect } from 'react';
import { db } from '../firebase/firebaseconfig';
import { collection, onSnapshot, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { Edit3, Trash2, ExternalLink, Plus, Search, Building2 } from 'lucide-react';
import AdminSidebar from './adminsidebar';

const AdminDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // 1. Real-time fetch of all projects
  useEffect(() => {
    const q = query(collection(db, "projects"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const projs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjects(projs);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 2. Handle Delete with Confirmation
  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, "projects", id));
        alert("Project deleted successfully.");
      } catch (error) {
        console.error("Error deleting:", error);
      }
    }
  };

  // 3. Filter projects by Search
  const filteredProjects = projects.filter(p => 
    p.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.client?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    
   
        <div className="container-fluid">
          
          {/* Header Section */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="fw-bold mb-1 text-dark">Project Dashboard</h2>
              <p className="text-muted small mb-0">Manage all ongoing and completed sites</p>
            </div>
            <Link to="/admin-panel-addproject" className="btn btn-success rounded-pill px-4 py-2 fw-bold shadow-sm d-flex align-items-center">
              <Plus size={18} className="me-2" /> Add New Project
            </Link>
          </div>

          {/* Stats & Search Bar */}
          <div className="card border-0 shadow-sm rounded-4 p-3 mb-4">
            <div className="row align-items-center">
              <div className="col-md-8">
                <div className="input-group bg-light rounded-pill px-3 py-1">
                  <span className="input-group-text bg-transparent border-0 text-muted"><Search size={18} /></span>
                  <input 
                    type="text" 
                    className="form-control bg-transparent border-0 shadow-none" 
                    placeholder="Search by project name or client..." 
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-4 text-end">
                <span className="badge bg-light text-dark border px-3 py-2 rounded-pill">
                  Total Projects: <strong>{projects.length}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Project Table */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4 py-3 text-muted small text-uppercase fw-bold">Project & Client</th>
                    <th className="py-3 text-muted small text-uppercase fw-bold">Location</th>
                    <th className="py-3 text-muted small text-uppercase fw-bold text-center">Progress</th>
                    <th className="py-3 text-muted small text-uppercase fw-bold text-end pe-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="4" className="text-center py-5">Loading projects...</td></tr>
                  ) : filteredProjects.length === 0 ? (
                    <tr><td colSpan="4" className="text-center py-5 text-muted">No projects found.</td></tr>
                  ) : (
                    filteredProjects.map((proj) => (
                      <tr key={proj.id}>
                        <td className="ps-4">
                          <div className="d-flex align-items-center">
                            <div className="bg-success-subtle p-2 rounded-3 me-3 text-success">
                              <Building2 size={20} />
                            </div>
                            <div>
                              <div className="fw-bold text-dark">{proj.title}</div>
                              <div className="text-muted small">{proj.client}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="small text-dark">{proj.city}</div>
                          <div className="text-muted" style={{ fontSize: '11px' }}>{proj.state}</div>
                        </td>
                        <td className="text-center" style={{ width: '150px' }}>
                          <div className="small fw-bold mb-1">{proj.percentage}%</div>
                          <div className="progress" style={{ height: '6px' }}>
                            <div 
                              className={`progress-bar rounded-pill ${Number(proj.percentage) === 100 ? 'bg-success' : 'bg-warning'}`} 
                              role="progressbar" 
                              style={{ width: `${proj.percentage}%` }}
                            ></div>
                          </div>
                        </td>
                        <td className="text-end pe-4">
                          <div className="d-flex justify-content-end gap-2">
                            
                            <Link 
                              to={`/admin-panel-edit-project/${proj.id}`} 
                              className="btn btn-sm btn-light rounded-circle text-dark border"
                              title="Edit Details"
                            >
                              <Edit3 size={16} />
                            </Link>
                            <button 
                              className="btn btn-sm btn-light rounded-circle text-danger border"
                              onClick={() => handleDelete(proj.id, proj.title)}
                              title="Delete Project"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
    
  );
};

export default AdminDashboard;